import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@components/common/Modal/Modal';
import Input from '@components/Form/Input/Input';
import Button from '@components/Button/Button';
import { applyServerValidationErrors } from '@/utils/apiValidationErrors';

export interface EditMentorForm {
    name: string;
    email: string;
    speciality: string;
}

export interface EditMentorModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialData: EditMentorForm | null;
    onSubmit?: (data: EditMentorForm) => void | Promise<void>;
    isSubmitting?: boolean;
}

const EditMentorModal = ({
    isOpen,
    onClose,
    initialData,
    onSubmit,
    isSubmitting = false,
}: EditMentorModalProps) => {
    const { t } = useTranslation('mentors');
    const {
        register,
        handleSubmit,
        reset,
        setError,
        clearErrors,
        formState: { errors },
    } = useForm<EditMentorForm>({
        defaultValues: {
            name: '',
            email: '',
            speciality: '',
        },
    });

    useEffect(() => {
        if (isOpen && initialData) {
            reset({
                name: initialData.name,
                email: initialData.email,
                speciality: initialData.speciality,
            });
        }
    }, [isOpen, initialData, reset]);

    const handleClose = () => {
        clearErrors();
        onClose();
    };

    const handleFormSubmit = async (data: EditMentorForm) => {
        clearErrors();
        try {
            await Promise.resolve(onSubmit?.(data));
        } catch (error) {
            applyServerValidationErrors<EditMentorForm>(error, setError);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title={t('modal.edit_title')}>
            <form
                id="edit-mentor-form"
                onSubmit={handleSubmit(handleFormSubmit)}
                noValidate
                className="overflow-y-auto p-4 2xl:p-5 flex flex-col gap-4"
            >
                <Input
                    label={t('modal.name_label')}
                    placeholder={t('modal.name_placeholder')}
                    required
                    registration={register('name', {
                        required: t('modal.validation.name_required'),
                        validate: value =>
                            value.trim().length > 0 ||
                            t('modal.validation.name_empty'),
                    })}
                    error={errors.name?.message}
                />

                <Input
                    label={t('modal.email_label')}
                    placeholder={t('modal.email_placeholder')}
                    required
                    type="email"
                    registration={register('email', {
                        required: t('modal.validation.email_required'),
                        pattern: {
                            value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                            message: t('modal.validation.email_invalid'),
                        },
                    })}
                    error={errors.email?.message}
                />

                <Input
                    label={t('modal.speciality_label')}
                    placeholder={t('modal.speciality_placeholder')}
                    required
                    registration={register('speciality', {
                        required: t('modal.validation.speciality_required'),
                        validate: value =>
                            value.trim().length > 0 ||
                            t('modal.validation.speciality_empty'),
                    })}
                    error={errors.speciality?.message}
                />
            </form>

            <div className="flex items-center justify-between border-t border-[var(--neutral-color)] px-4 2xl:px-5 py-2 2xl:py-3">
                <Button type="button" variant="dark" onClick={handleClose} disabled={isSubmitting}>
                    {t('modal.cancel_button')}
                </Button>

                <Button type="submit" variant="primary" form="edit-mentor-form" disabled={isSubmitting}>
                    {isSubmitting ? t('modal.updating_button') : t('modal.save_button')}
                </Button>
            </div>
        </Modal>
    );
};

export default EditMentorModal;
